import { Alert, Box, Button } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useBoolean } from 'usehooks-ts';

import { Icon } from '@/components/Icon/Icon';
import { FullPageLoaderOverlay } from '@/components/Layout/FullPageLoader';

import { ROUTE } from '@/constants/routes';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { useMutationWithUserData } from '@/hooks/useMutationWithUserData';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectConfigsState } from '@/redux/configs/selectors';
import { fetchAccountTypes, fetchUserHolders } from '@/redux/user-data';
import { selectAccountTypes } from '@/redux/user-data/selectors';

import { AccountTypeDetails, AccountTypeStatus } from '@/types/api/account';

import { getAPIErrorMessage } from '@/utils/api';
import { getDataTestId } from '@/utils/browser';
import { isAdultUser, isMinorUser, isRightStartUser } from '@/utils/user';

import { batchSetAccountTypes } from '../api/account';
import BaseAccountTypeHOC from '../hoc/BaseAccountTypeHOC';
import { AccountTypeDetailsDrawer } from './AccountTypeDetailsDrawer';
import { JointProceedButtons } from './JointProcessButtons';

import { ActionBar, CardStack } from './AccountTypeForm.styled';

export function AccountTypeForm() {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const loggedInUser = useAppSelector(selectLoggedInUser);
  const [currentSlide, setCurrentSlide] = useState<{
    index: number;
    accountType: AccountTypeDetails;
  }>();
  const {
    value: isDrawerOpen,
    setTrue: showDrawer,
    setFalse: hideDrawer,
  } = useBoolean(false);
  const accountTypesData = useAppSelector(selectAccountTypes);
  const isMinorUserFlag = isMinorUser(loggedInUser);

  const priorityAccountName = t(
    'accountTypeForm.priorityAccountName',
  ).toLowerCase();

  const accountTypes = useMemo(() => {
    const isPriority = (name: string) =>
      name.toLowerCase().includes(priorityAccountName);

    return [...accountTypesData].sort(
      (a, b) => Number(isPriority(b.name)) - Number(isPriority(a.name)),
    );
  }, [accountTypesData, priorityAccountName]);

  const isRightStartUserFlag = isRightStartUser(loggedInUser);
  const isAdultUserFlag = isAdultUser(loggedInUser);

  const [selectedAccounts, setSelectedAccounts] = useState<
    AccountTypeDetails[]
  >([]);
  const { accountTypeMaxAmount } = useAppSelector(selectConfigsState);

  const { mutate, isPending, error, reset } = useMutationWithUserData({
    mutationFn: batchSetAccountTypes,
    onFinished: () => {
      if (isAdultUserFlag && loggedInUser?.operationId) {
        dispatch(fetchUserHolders(loggedInUser?.operationId));
      }
    },
  });

  useEffect(() => {
    if (accountTypes.length) {
      setCurrentSlide({
        index: 0,
        accountType: accountTypes[0],
      });
    }
  }, [accountTypes]);

  useEffect(() => {
    if (isRightStartUserFlag && accountTypes.length === 1) {
      setSelectedAccounts(accountTypes);
    }
  }, [accountTypes, isRightStartUserFlag]);

  useEffect(() => {
    dispatch(fetchAccountTypes());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedName = useMemo(() => {
    if (selectedAccounts.length === 1) {
      const accountType = accountTypes.find(
        item => item.id === selectedAccounts[0].id,
      );
      return accountType ? accountType.description : '';
    }
    return '';
  }, [accountTypes, selectedAccounts]);

  const setAccounts = (
    accounts: AccountTypeDetails[],
    data: AccountTypeDetails,
  ) => {
    if (accounts.some(account => account.id === data.id)) {
      const newAccounts = accounts.filter(account => account.id !== data.id);
      return newAccounts;
    } else {
      if (isRightStartUserFlag) {
        return [data];
      }
      const newSelectedAccounts = [data, ...accounts];
      if (accountTypeMaxAmount > 0) {
        return newSelectedAccounts.slice(0, accountTypeMaxAmount);
      }
      return newSelectedAccounts;
    }
  };

  const toggleSelect = (data: AccountTypeDetails) => {
    const accountToSet = setAccounts(selectedAccounts, data);
    setSelectedAccounts(accountToSet);
  };

  const toggleSelectCurrentSlide = () => {
    if (currentSlide?.accountType.id) {
      toggleSelect(currentSlide.accountType);
    }
  };

  const onProceedCurrentAccountType = () => {
    if (currentSlide?.accountType.status === AccountTypeStatus.Active) {
      reset();
      hideDrawer();
      mutate([currentSlide.accountType.id]);
    }
  };

  const onProceed = () => {
    const accountToSend = selectedAccounts.map(account => account.id);

    if (accountToSend.length) {
      reset();
      hideDrawer();
      mutate(accountToSend);
    }
  };

  const handleContinue = () => {
    navigate(ROUTE.ADD_ADULT_DETAIL, { replace: true });
  };

  const handleNotNow = () => {
    navigate(ROUTE.ADD_ADULT_CONTACT, { replace: true });
  };

  const onSeeDetails = (data: AccountTypeDetails, index: number) => {
    setCurrentSlide({
      accountType: data,
      index: index,
    });
    showDrawer();
  };

  const isOnlyAccountType =
    accountTypes.filter(item => item.status === AccountTypeStatus.Active)
      .length === 1;

  const onToggleSelect = (data: AccountTypeDetails) => {
    if (isRightStartUserFlag && accountTypes.length === 1) {
      return;
    }
    toggleSelect(data);
  };

  return (
    <>
      <CardStack>
        <BaseAccountTypeHOC
          accountTypes={accountTypes}
          selectedAccountTypes={selectedAccounts}
          onSeeDetails={onSeeDetails}
          showCheckbox={!isRightStartUserFlag || accountTypes.length > 1}
          toggleSelect={onToggleSelect}
        />
      </CardStack>
      {error && (
        <Box mb={6}>
          <Alert icon={<Icon name="alert" />} color="error" variant="standard">
            {getAPIErrorMessage(error)}
          </Alert>
        </Box>
      )}
      <ActionBar
        isBackdropEnabled={
          accountTypes.length > 1 && !!currentSlide?.accountType.additionalInfo
        }
      >
        {isMinorUserFlag ? (
          <JointProceedButtons
            onContinue={handleContinue}
            onNotNow={handleNotNow}
          />
        ) : (
          <Button
            {...getDataTestId(`account-type-proceed-button`)}
            variant="contained"
            fullWidth
            onClick={onProceed}
            disabled={!selectedAccounts.length}
          >
            {selectedName
              ? t('accountTypeForm.button.proceedWith', { name: selectedName })
              : t('accountTypeForm.button.proceed')}
          </Button>
        )}
      </ActionBar>
      {currentSlide?.accountType && (
        <AccountTypeDetailsDrawer
          data={currentSlide.accountType}
          open={isDrawerOpen}
          onClose={hideDrawer}
          onSelect={toggleSelectCurrentSlide}
          isSelected={selectedAccounts.some(
            account => account.id === currentSlide.accountType.id,
          )}
          onProceed={onProceedCurrentAccountType}
          onContinue={handleContinue}
          onNotNow={handleNotNow}
          isOnlyAccountType={isOnlyAccountType}
          isDisabledSelect={currentSlide.accountType.reachedMaxAccountsPerType}
        />
      )}
      {isPending && <FullPageLoaderOverlay />}
    </>
  );
}

export default AccountTypeForm;
